import { FormattedMessage, IntlShape, MessageDescriptor, useIntl } from 'react-intl';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { MarketCode } from '@tink/config/src/markets';
import { Header } from '../../../components/organisms/Header/Header';
import { ProviderViewSet } from '../../../utilities/provider-view-set';
import { H1 } from '../../../components/atoms/Heading/Heading';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import {
  AuthenticationUserType,
  Provider,
  ProviderAccessType,
  ProviderEntity,
} from '../../../models/provider';
import { ProviderCapability } from '../../../models/providerCapability';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { Subtitle } from '../../../components/atoms/Subtitle/Subtitle';
import { SelectableOption } from '../../../components/screens/molecules/SelectableOption/SelectableOption';
import { PageView } from '../../../models/analytics';

import { StyledLogo } from '../../../components/screens/molecules/SelectableOption/molecules/ListItem/ListItem.styles';
import { useVersion } from '../../../components/ApplicationProviders/VersionProvider/VersionProvider';
import { usePageViewEvents } from '../../Analytics/usePageViewEvents';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { messages } from './messages';

type CapabilityToMessage = {
  [key in ProviderCapability]: MessageDescriptor | undefined;
};

const capabilityDescriptions: CapabilityToMessage = {
  [ProviderCapability.CHECKING_ACCOUNTS]: messages.capabilityCheckingAccounts,
  [ProviderCapability.CREDIT_CARDS]: messages.capabilityCreditCards,
  [ProviderCapability.EINVOICES]: messages.capabilityEinvoices,
  [ProviderCapability.IDENTITY_DATA]: messages.capabilityIdentityData,
  [ProviderCapability.INVESTMENTS]: messages.capabilityInvestments,
  [ProviderCapability.LIST_BENEFICIARIES]: undefined,
  [ProviderCapability.LOANS]: messages.capabilityLoans,
  [ProviderCapability.MORTGAGE_AGGREGATION]: undefined,
  [ProviderCapability.MORTGAGE_LOAN]: messages.capabilityMortgageLoan,
  [ProviderCapability.PAYMENTS]: undefined,
  [ProviderCapability.SAVINGS_ACCOUNTS]: messages.capabilitySavingsAccounts,
  [ProviderCapability.TRANSFERS]: undefined,
  [ProviderCapability.UNKNOWN]: messages.capabilityUnknown,
};

export const getCapabilityDescription = (intl: IntlShape, capabilities: ProviderCapability[]) => {
  const descriptions = Array.from(capabilities)
    .sort()
    .map((capability) => capabilityDescriptions[capability])
    .filter((description) => Boolean(description))
    .map((description) => intl.formatMessage(description as MessageDescriptor));

  return descriptions.join(', ');
};

type OnProviderItemClick = ({ accessType }: { accessType: ProviderAccessType }) => void;

export type AccessTypePickerProps = {
  onBack: () => void;
  authenticationUserType: AuthenticationUserType;
  financialInstitutionId: string;
  providers: ProviderEntity[];
  onProviderItemClick: OnProviderItemClick;
};

export const AccessTypePicker = ({
  onBack,
  onProviderItemClick,
  financialInstitutionId,
  authenticationUserType,
  providers: allProviders,
}: AccessTypePickerProps) => {
  const {
    version: { version },
  } = useVersion();
  const intl = useIntl();
  const { market } = useUrlQuery();

  usePageViewEvents({
    pageView: PageView.ACCESS_TYPE_SELECTION_SCREEN,
  });

  const providers = allProviders.filter((provider) => {
    return (
      provider.financialInstitutionId === financialInstitutionId &&
      provider.authenticationUserType === authenticationUserType
    );
  });

  const accessTypeGroup = new ProviderViewSet(providers, version).groupByAccessType();

  const openBankingGroup = accessTypeGroup.find(
    (group) => group.title === ProviderAccessType.OPEN_BANKING
  );

  const otherGroup = accessTypeGroup.find((group) => group.title === ProviderAccessType.OTHER);

  return (
    <ScreenWrapper>
      <Header onBack={onBack} pageView={PageView.ACCESS_TYPE_SELECTION_SCREEN} />
      <ContentBody>
        <H1>
          <FormattedMessage {...messages.accountTypesHeading} />
        </H1>
        {market !== MarketCode.US && (
          <Subtitle>
            <FormattedMessage {...messages.accountTypesDescription} />
          </Subtitle>
        )}
        <Spacer size="normal" />
        {openBankingGroup && (
          <SelectableOption
            title={intl.formatMessage(messages.accessTypeHeaderOpenBanking)}
            text={getCapabilityDescription(intl, openBankingGroup.providers[0].capabilities)}
            Icon={<StyledLogo src={Provider.getIcon(openBankingGroup.providers[0])} alt="" />}
            onClick={() => {
              onProviderItemClick({ accessType: ProviderAccessType.OPEN_BANKING });
            }}
          />
        )}

        {otherGroup && (
          <SelectableOption
            title={intl.formatMessage(messages.accessTypeHeaderOther)}
            text={getCapabilityDescription(intl, otherGroup.providers[0].capabilities)}
            Icon={<StyledLogo src={Provider.getIcon(otherGroup.providers[0])} alt="" />}
            onClick={() => {
              onProviderItemClick({ accessType: ProviderAccessType.OTHER });
            }}
          />
        )}
      </ContentBody>
    </ScreenWrapper>
  );
};
